<template>
  <div class=" flex flex-col">
    <div class="w-full md:w-auto flex flex-col items-center justify-evenly md:flex-row pb-6 md:justify-between gap-4">
      <div class="flex w-full md:w-auto justify-between gap-4">
        <PrintTableComponent :title="isPlatformView ? $t('user.list.title') : $t('user.list.titleAccount')" :fields="translatedFields" :items="processedItems"
          @print-started="() => console.log($t('user.list.printStarted'))"
          @print-completed="() => console.log($t('user.list.printCompleted'))"
          @print-failed="(error) => console.error($t('user.list.printFailed'), error)" />

          <RouterLink to="/user-new">
          <ButtonWhite class="md:hidden flex items-center gap-2">
            {{ $t('user.buttons.add') }}
            <PlusIcon class="w-5 inline-block" />
          </ButtonWhite>
        </RouterLink>
      </div>

      <div class="flex md:gap-4 w-full md:w-auto justify-between">
        <RouterLink to="/user-new">
          <ButtonWhite class="hidden md:flex items-center gap-2">
            {{ $t('user.buttons.add') }}
            <PlusIcon class="w-5 inline-block" />
          </ButtonWhite>
        </RouterLink>

        <!-- TODO API bug de account type, role  -->

      </div>
    </div>

    <TitlePrimary :title="isPlatformView ? $t('user.list.title') : $t('user.list.titleAccount')" class="w-full" />

    <p v-if="loadError" role="alert" class="text-red-500 py-3">{{ loadError }}</p>

    <div class="md:hidden">
      <CardTable v-if="isSuperadmin" :fields="translatedFields" :items="processedItems.length > 0 ? [processedItems[currentItemIndex]] : []"
        :route="detailRoute" :isEdit="true"
        editRoute="new-user" />
      <CardTable v-else :fields="translatedFields" :items="processedItems.length > 0 ? [processedItems[currentItemIndex]] : []"
        :route="detailRoute" :isEdit="true"
        editRoute="new-user" />
      <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
        :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
        @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
    </div>

    <div class="hidden md:block">
      <TableComponent :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange" :fields="translatedFields" :items="processedItems" :route="detailRoute" :isEdit="true"
        editRoute="new-user" />

      <PaginateComponent :totalPages="totalPages" :paginationInfo="paginationInfo" :modelValue="currentPage" @update:modelValue="handlePageChange" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useTableFilters } from '@/composables/useTableFilters';
import axios from 'axios';
import { hasAnyRole } from '@/services/userRoles';

import TitlePrimary from '@/components/title/title-primary.vue';
import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import { usePaginationInfo } from '@/composables/usePaginationInfo';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import PrintTableComponent from '@/components/print/PrintTableComponent.vue';
import PlusIcon from '@/assets/svg/PlusIcon.vue';
import { useI18n } from 'vue-i18n';
import CardTable from '@/components/table/CardTable.vue';
import MobilePaginateComponent from '@/components/paginate/MobilePaginateComponent.vue';

const isSuperadmin = hasAnyRole(['superadmin']);
const isBackOffice = hasAnyRole(['admin']) && !isSuperadmin;
const isPlatformView = isSuperadmin || isBackOffice;
// Back Office abre la misma ficha que el responsable; el listado sigue siendo
// el de plataforma, que deja fuera a los responsables (ADS-0119).
const detailRoute = '/user-detail';

// --- Estado ---
const items = ref([]);
const loadError = ref('');
const currentPage = ref(1);
const totalPages = ref(1);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();
const isSearchActive = ref(false);
const currentItemIndex = ref(0);
const totalItems = ref(0);
const { t } = useI18n();

const capitalizeFirstLetter = (value = '') => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};


const fields = [
  { key: 'name', label: 'NOMBRE' },
  { key: 'email', label: 'EMAIL' },
  // La cuenta cliente dice de quién es cada persona (ADS-0119).
  { key: 'account', label: 'CUENTA' },
  { key: 'role', label: 'TIPO DE CUENTA' },
  // { key: 'role', label: 'ROL' },
  { key: 'status', label: 'ESTADO' }
];

const translatedFields = computed(() => fields.map(field => ({
  ...field,
  label: t(`user.fields.${field.key.toLowerCase()}`)
})));

// --- filtros de cabecera ---
const USER_ROLES = [...(isBackOffice ? [] : ['superadmin']), 'admin', 'comercial', 'horizontal', 'vertical', 'both', 'delegacion', 'worker'];
const USER_STATUSES = ['active', 'inactive', 'deleted'];

const filterDefinitions = computed(() => [
  { key: 'name', type: 'text' },
  { key: 'email', type: 'text' },
  { key: 'account', type: 'text' },
  {
    key: 'role',
    type: 'select',
    options: USER_ROLES.map((value) => ({ value, label: t(`common.requestTypes.${value}`) })),
  },
  {
    key: 'status',
    type: 'select',
    options: USER_STATUSES.map((value) => ({ value, label: t(`common.requestStatus.${value}`) })),
  },
]);

const { filters, isFiltering, filterError, runSearch, onFilterChange } = useTableFilters({
  endpoint: () => (isBackOffice ? '/user/directory' : isSuperadmin ? '/user/superadmin/search' : '/admin/worker/search'),
  perPage: 10,
  translate: t,
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    loadError.value = '';
    currentItemIndex.value = 0;
    items.value = page.data || [];
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    totalItems.value = page.total ?? 0;
    currentPage.value = page.current_page || 1;
    isSearchActive.value = true;
  },
  onCleared: () => {
    isSearchActive.value = false;
    currentPage.value = 1;
    fetchDataFromApi();
  },
});



// Computed property to process items
const processedItems = computed(() => {
  return items.value.map(user => {
    // Un cliente trae su clasificación resuelta en `type_label`; una persona
    // solo tiene sus roles del panel, que se siguen mostrando igual.
    const roleNames = user.type_label
      || (user.roles && Array.isArray(user.roles)
        ? user.roles
            .map(role => capitalizeFirstLetter(role.name))
            .filter(name => name)
            .join(', ')
        : '');
    
    // Combine name and surname for the NOMBRE column
    const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
    
    // Create account type with role information
    const accountType = roleNames ? `Cuenta ${roleNames}` : 'Sin rol';
    
    return {
      ...user, // Spread existing user properties
      name: fullName, // Replace name with full name (name + surname)
      accountType: accountType, // Add account type with role info
      role: roleNames // Add the new string property
    };
  });
});

// --- funciones ---
const fetchDataFromApi = async () => {
  isSearchActive.value = false;
  currentItemIndex.value = 0;
  loadError.value = '';
  let response
  try {
    response = await axios.get(
      `${import.meta.env.VITE_API_URL}${isBackOffice ? '/user/directory' : isSuperadmin ? '/user/superadmin/list' : '/user/list'}`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          per_page: 10,
          page: currentPage.value
        }
      }
    );

    console.log(response.data.value)
    if (response.data?.value.data) {
      console.log('axios', response.data.value.data)

      items.value = response.data.value.data
      totalPages.value = response.data.value.last_page;
      setPaginationInfo(response.data.value);
      totalItems.value = response.data.value.total ?? 0;
      console.log('items', items.value)
    }
  } catch (error) {
    items.value = [];
    totalPages.value = 1;
    totalItems.value = 0;
    loadError.value = t(error.response?.status === 403 ? 'search.forbidden' : 'search.failed');
    console.error('Error fetching data:', error);
  }
};

// Manejo de cambio de página
const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    await fetchDataFromApi();
  }

};

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};


onMounted(() => {
  fetchDataFromApi();
});

</script>


<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
